/*
 * SF Outfit Cart Sync v1.0 — add-on for Skinnify's existing cart.js.
 *
 * Outfit lines are identified ONLY by the pair of private line properties
 *   _sf_outfit_group = unique outfit instance id
 *   _sf_outfit_role  = top | leggings
 *
 * Preserves the original BOGO and discounted-pair quantity overrides by
 * delegating every non-outfit operation to the already-installed handler.
 * Does not calculate or apply checkout discounts; SMART Functions handles that.
 */
(function () {
  'use strict';

  if (window.__sfOutfitCartSyncV1) return;
  var CartItemsClass = window.customElements && customElements.get('cart-items');
  if (!CartItemsClass) {
    console.warn('[SF Outfit Cart] cart-items was not defined when outfit sync loaded.');
    return;
  }

  window.__sfOutfitCartSyncV1 = true;
  var proto = CartItemsClass.prototype;
  var previousUpdateQuantity = proto.updateQuantity;
  var busy = false;

  function rootPath() {
    var root = window.Shopify && window.Shopify.routes && window.Shopify.routes.root;
    if (!root) root = '/';
    return root.replace(/\/?$/, '/');
  }

  function groupOf(item) {
    var props = item && item.properties || {};
    return String(props._sf_outfit_group || '').trim();
  }

  function roleOf(item) {
    var props = item && item.properties || {};
    return String(props._sf_outfit_role || '').trim().toLowerCase();
  }

  function getOutfitItems(cart, group) {
    var matches = ((cart && cart.items) || []).filter(function (item) {
      return groupOf(item) === group && (roleOf(item) === 'top' || roleOf(item) === 'leggings');
    });
    return {
      all: matches,
      top: matches.filter(function (item) { return roleOf(item) === 'top'; }),
      leggings: matches.filter(function (item) { return roleOf(item) === 'leggings'; })
    };
  }

  function hasExactPair(pair) {
    return pair.top.length === 1 && pair.leggings.length === 1;
  }

  async function requestJson(url, options) {
    var response = await fetch(url, Object.assign({credentials: 'same-origin'}, options || {}));
    var result = await response.json().catch(function () { return {}; });
    if (!response.ok || result.errors) {
      var message = (result && (result.description || result.message || result.errors)) || 'Cart could not be updated.';
      var error = new Error(typeof message === 'string' ? message : 'Cart could not be updated.');
      error.status = response.status;
      throw error;
    }
    return result;
  }

  function sectionList() {
    var sections = ['cart-drawer', 'cart-icon-bubble'];
    var mainItems = document.getElementById('main-cart-items');
    var mainFooter = document.getElementById('main-cart-footer');
    if (mainItems && mainItems.dataset.id) sections.push(mainItems.dataset.id);
    if (mainFooter && mainFooter.dataset.id) sections.push(mainFooter.dataset.id);
    // Shopify supports a maximum of five sections per bundled render request.
    return sections.filter(function (value, index) { return sections.indexOf(value) === index; }).slice(0, 5);
  }

  function getCart() {
    return requestJson(rootPath() + 'cart.js', {
      method: 'GET', headers: {Accept: 'application/json'}, cache: 'no-store'
    });
  }

  function updateByKeys(updates) {
    return requestJson(rootPath() + 'cart/update.js', {
      method: 'POST',
      headers: {'Content-Type': 'application/json', Accept: 'application/json'},
      body: JSON.stringify({
        updates: updates,
        sections: sectionList(),
        sections_url: window.location.pathname + window.location.search
      })
    });
  }

  function buildUpdates(pair, quantity) {
    var updates = {};
    pair.all.forEach(function (item) {
      // Use the exact Shopify cart key, not the variant ID or line index.
      updates[String(item.key)] = quantity;
    });
    return updates;
  }

  function verifiedQuantity(cart, group, desired) {
    var pair = getOutfitItems(cart, group);
    if (desired === 0) return pair.all.length === 0;
    return hasExactPair(pair) && pair.all.every(function (item) {
      return Number(item.quantity) === desired;
    });
  }

  async function synchronize(group, quantity) {
    var attempts = 0;
    while (attempts < 2) {
      attempts += 1;
      var before = await getCart();
      var pair = getOutfitItems(before, group);
      if (!pair.all.length) throw new Error('This outfit is no longer in the cart. Please refresh the page.');
      if (quantity > 0 && !hasExactPair(pair)) {
        throw new Error('This outfit is incomplete. Remove it and add both pieces again.');
      }
      var beforeQuantities = {};
      pair.all.forEach(function (item) { beforeQuantities[roleOf(item)] = Number(item.quantity); });

      try {
        var changed = await updateByKeys(buildUpdates(pair, quantity));
        if (verifiedQuantity(changed, group, quantity)) return changed;

        // If Shopify removed only one of the two lines, finish deleting the
        // remaining member instead of leaving an incomplete outfit behind.
        if (quantity === 0) {
          var remaining = getOutfitItems(changed, group);
          if (remaining.all.length) {
            var removedRest = await updateByKeys(buildUpdates(remaining, 0));
            if (verifiedQuantity(removedRest, group, 0)) return removedRest;
            var removalError = new Error('Could not remove the entire outfit. Please remove the incomplete outfit before checkout.');
            removalError.cartState = removedRest;
            throw removalError;
          }
        }

        // Shopify can enforce inventory limits without reaching the requested quantity.
        // Restore the original pair so a partial outfit is not left by this interaction.
        var current = getOutfitItems(changed, group);
        var restore = {};
        current.all.forEach(function (item) {
          var oldQty = beforeQuantities[roleOf(item)];
          if (typeof oldQty === 'number') restore[String(item.key)] = oldQty;
        });
        var rolledBack = Object.keys(restore).length ? await updateByKeys(restore) : changed;
        var inventoryError = new Error('Unable to update both outfit pieces to that quantity. Please check stock and try again.');
        inventoryError.cartState = rolledBack;
        throw inventoryError;
      } catch (error) {
        if (error.status === 409 && attempts < 2) continue;
        throw error;
      }
    }
    throw new Error('The cart changed while updating. Please try again.');
  }

  function parseSection(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function renderCartSections(state) {
    if (!state || !state.sections) return;
    var sections = state.sections;
    var drawer = document.querySelector('cart-drawer');

    if (sections['cart-drawer'] && drawer) {
      var documentHTML = parseSection(sections['cart-drawer']);
      var nextInner = documentHTML.querySelector('cart-drawer .drawer__inner, #CartDrawer .drawer__inner');
      var oldInner = drawer.querySelector('.drawer__inner');
      if (nextInner && oldInner) {
        var priorOverlay = drawer.querySelector('#CartDrawer-Overlay');
        oldInner.replaceWith(nextInner);
        // Rebind only if replacement actually replaced the overlay node.
        var overlay = drawer.querySelector('#CartDrawer-Overlay');
        if (overlay && overlay !== priorOverlay && typeof drawer.close === 'function') {
          overlay.addEventListener('click', drawer.close.bind(drawer));
        }
      }
      drawer.classList.toggle('is-empty', Number(state.item_count) === 0);
      drawer.toggleAttribute('is-empty', Number(state.item_count) === 0);
    }

    if (sections['cart-icon-bubble']) {
      var bubbleDoc = parseSection(sections['cart-icon-bubble']);
      var newBubble = bubbleDoc.getElementById('cart-icon-bubble');
      var oldBubble = document.getElementById('cart-icon-bubble');
      if (newBubble && oldBubble) {
        oldBubble.replaceWith(newBubble);
        if (drawer && typeof drawer.setHeaderCartIconAccessibility === 'function') {
          drawer.setHeaderCartIconAccessibility();
        }
      }
    }

    function replaceJsContents(sectionId, currentRootId) {
      if (!sectionId || !sections[sectionId]) return;
      var doc = parseSection(sections[sectionId]);
      var source = doc.querySelector('#' + currentRootId + ' .js-contents');
      var target = document.querySelector('#' + currentRootId + ' .js-contents');
      if (source && target) target.innerHTML = source.innerHTML;
    }

    var mainCartItems = document.getElementById('main-cart-items');
    var mainCartFooter = document.getElementById('main-cart-footer');
    if (mainCartItems && mainCartItems.dataset.id) replaceJsContents(mainCartItems.dataset.id, 'main-cart-items');
    if (mainCartFooter && mainCartFooter.dataset.id) replaceJsContents(mainCartFooter.dataset.id, 'main-cart-footer');
  }

  function setBusy(value, instance) {
    busy = value;
    var drawer = document.querySelector('cart-drawer');
    if (drawer) {
      drawer.classList.toggle('is-loading', value);
      if (value) drawer.setAttribute('aria-busy', 'true');
      else drawer.removeAttribute('aria-busy');
      var button = drawer.querySelector('#CartDrawer-Checkout');
      if (button) button.disabled = value || drawer.classList.contains('is-empty');
    }
    if (instance && instance.isConnected) {
      instance.classList.toggle('cart__items--disabled', value);
    }
  }

  function showError(message) {
    var target = document.getElementById('CartDrawer-CartErrors') || document.getElementById('cart-errors');
    if (target) target.textContent = message || '';
    if (!target && message) window.console.error('[SF Outfit Cart]', message);
  }

  function announceSuccess() {
    var status = document.getElementById('CartDrawer-LineItemStatus');
    if (status) {
      status.textContent = 'Outfit quantity updated. Both pieces have the same quantity.';
      status.setAttribute('aria-hidden', 'false');
    }
  }

  function afterUpdate(state, group, quantity) {
    renderCartSections(state);
    showError('');
    if (quantity > 0) announceSuccess();
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'cart-items',
        cartData: state,
        outfitGroup: group
      });
    }
  }

  async function handleOutfitUpdate(instance, group, desired) {
    if (busy) return;
    setBusy(true, instance);
    showError('');
    try {
      var newState = await synchronize(group, desired);
      afterUpdate(newState, group, desired);
    } catch (error) {
      if (error.cartState) renderCartSections(error.cartState);
      else {
        // Reset a manually edited quantity field until the next user action.
        var row = Array.prototype.find.call(document.querySelectorAll('[data-sf-outfit-group]'), function (el) {
          return el.dataset.sfOutfitGroup === group && el.dataset.sfOutfitRole === 'top';
        });
        var input = row && row.querySelector('.quantity__input');
        if (input) input.value = input.getAttribute('value') || input.defaultValue;
      }
      showError(error.message || 'Unable to change your outfit. Please try again.');
      console.error('[SF Outfit Cart] Failed to sync outfit:', error);
    } finally {
      setBusy(false, instance);
    }
  }

  proto.updateQuantity = function (line, quantity, event, name, variantId) {
    var args = arguments;
    var instance = this;
    var row = document.getElementById('CartDrawer-Item-' + line) || document.getElementById('CartItem-' + line);
    var group = row && row.getAttribute('data-sf-outfit-group');
    var role = row && row.getAttribute('data-sf-outfit-role');
    var desired = Math.max(0, parseInt(quantity, 10) || 0);

    if (group && (role === 'top' || role === 'leggings')) {
      return handleOutfitUpdate(instance, group, desired);
    }

    // The normal /cart page may not have the drawer's custom data attributes.
    // Read the current Shopify cart before deciding whether to delegate.
    if (this.tagName === 'CART-ITEMS') {
      getCart().then(function (cart) {
        var item = cart.items[Number(line) - 1];
        var foundGroup = groupOf(item);
        var foundRole = roleOf(item);
        if (foundGroup && (foundRole === 'top' || foundRole === 'leggings')) {
          return handleOutfitUpdate(instance, foundGroup, desired);
        }
        return previousUpdateQuantity.apply(instance, args);
      }, function (error) {
        console.warn('[SF Outfit Cart] Could not identify cart-page item:', error);
        // Don't break unrelated regular cart items if the lookup fails.
        return previousUpdateQuantity.apply(instance, args);
      });
      return;
    }

    return previousUpdateQuantity.apply(this, arguments);
  };
})();
